// src/components/OfficeCopyPanel.js
//
// WO-001 A3.2 (2026-09-14, task list section 246): the outgoing officer's marked copy. Bob's ruling
// (design A3.2, A3.6): an outgoing General or Local Chairperson may take his records - every document,
// EVERY PAGE marked 'GC COPY' / 'LC COPY', large, bold, 20% shade, with the date the copy was made -
// and not live access. This panel lists the terms the signed-in person holds or held (officeTerms by
// holderUid) and, for each, one button that calls exportOfficeCopy. What comes back is the manifest:
// what was stamped (with a link to each file, readable by him alone under storage.rules
// exports/{uid}/...), what was out of scope and why, what was REFUSED and why. Nothing is composed
// here; the function's answer is shown as it says it.
import React, { useEffect, useState } from 'react';
import { db, storage } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COPY_MARK } from '../data/officeCopy';
import RulingRef from './RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export default function OfficeCopyPanel({ user }) {
  const [terms, setTerms] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);   // { termId, data | error }
  const [links, setLinks] = useState({});
  // Forensic audit 2026-10-16: a failed read used to become [] and the panel vanished - an outgoing officer was told,
  // by silence, that he holds no term to take a copy of. The failure is now shown.
  const [readErr, setReadErr] = useState('');
  useEffect(() => {
    if (!user || !user.uid) return undefined;
    setReadErr('');
    const unsub = onSnapshot(query(collection(db, 'officeTerms'), where('holderUid', '==', user.uid)),
      s => { setTerms(s.docs.map(d => ({ termId: d.id, ...d.data() }))); setReadErr(''); },
      e => { setTerms(null); setReadErr('Your terms of office could not be read (' + ((e && (e.code || e.message)) || 'refused') + '). This is NOT "no term held" - reload the page to take your marked copy.'); });
    return () => unsub();
  }, [user]);
  if (user && readErr) {
    return (
      <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14, padding: '10px 14px', fontSize: 12, color: C.bad }}>
        &#9888; My records as an officer - the marked copy: {readErr}
      </div>
    );
  }
  if (!user || !terms || terms.length === 0) return null;

  const take = async (t) => {
    setBusy(t.termId); setResult(null); setLinks({});
    try {
      const fn = httpsCallable(getFunctions(), 'exportOfficeCopy');
      const res = await fn({ termId: t.termId });
      setResult({ termId: t.termId, data: res.data });
      const l = {};
      for (const w of res.data.written) { try { l[w.path] = await getDownloadURL(ref(storage, w.path)); } catch (e) { l[w.path] = null; } }
      setLinks(l);
    } catch (e) { setResult({ termId: t.termId, error: (e && e.message) || String(e) }); }
    setBusy('');
  };
  const markOf = (t) => (t.officeId && /^LC/i.test(t.officeId)) ? COPY_MARK.local_committee : COPY_MARK.general_committee;

  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>My records as an officer - the marked copy</div>
        <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>WO-001 A3.2: an outgoing officer takes a copy, not access. Every document and every page is marked {COPY_MARK.general_committee} / {COPY_MARK.local_committee}, large, at 20%, with the date the copy was made. A file that cannot be marked page by page is refused and named. Drafts are never included.<RulingRef user={user} n={56} why="A draft belongs to its author alone until he submits it for representation." /> The export is an audit event.</div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim }}>
        {terms.map(t => (
          <div key={t.termId} style={{ borderTop: '1px solid ' + C.line, padding: '6px 0', display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ color: C.text, flex: 1 }}>{t.officeId} - term {t.termId}{t.method === 'interim' ? ' (acting)' : ''} - {t.startedOn || 'start not on record'} to {t.endedOn || 'today'}</span>
            <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => take(t)}>{busy === t.termId ? 'Marking every page…' : 'Take my ' + markOf(t)}</button>
          </div>
        ))}
        {result && result.error ? <div style={{ color: C.bad, marginTop: 8 }}>Refused: {result.error}</div> : null}
        {result && result.data ? (
          <div style={{ marginTop: 8, borderTop: '1px solid ' + C.line, paddingTop: 8 }}>
            <div style={{ color: C.ok }}>{result.data.mark} made {result.data.copyMadeOn}: {result.data.claims} claim(s) in scope, {result.data.written.length} file(s), {result.data.pages} page(s) marked, {result.data.refused.length} refused.</div>
            {result.data.written.map(w => (
              <div key={w.path} style={{ marginTop: 3 }}>
                {links[w.path] ? <a href={links[w.path]} target="_blank" rel="noreferrer" style={{ color: C.accent }}>{w.name}</a> : <span style={{ color: C.text }}>{w.name}</span>} <span>({w.pages} page{w.pages === 1 ? '' : 's'})</span>
              </div>
            ))}
            {result.data.refused.length ? (
              <div style={{ marginTop: 6, color: C.accent }}>REFUSED - a failure to mark is a failure to export:
                {result.data.refused.map((r, i) => <div key={i} style={{ marginLeft: 12 }}>{r.claimId}: {r.name} - {r.why}</div>)}
              </div>
            ) : null}
            {result.data.outOfScope && result.data.outOfScope.length ? (
              <details style={{ marginTop: 6 }}><summary style={{ cursor: 'pointer' }}>{result.data.outOfScope.length} claim(s) of the office not in this copy, and why</summary>
                {result.data.outOfScope.map(o => <div key={o.id} style={{ marginLeft: 12 }}>{o.id}: {o.why}</div>)}
              </details>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
